import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormControl, ValidatorFn, Validators } from "@angular/forms";
import { Observable, Subscription } from "rxjs";

@Component({
    selector: "app-search-box",
    templateUrl: "./search-box.component.html",
    styleUrls: ["./search-box.component.scss"],
})
export class SearchBoxComponent implements OnInit, OnDestroy {
    @Input() public placeholder = "";
    @Input() public startingValue?: string;
    @Input() public clearAfterSubmit = false;
    @Input() public syncValidators: ValidatorFn[] = [];

    @Input() public onChangeTriggered = false;

    /**
     * This event is sent from outside the search box.
     * It will be triggered when we have a new value to search that wasn't triggered using the component itself.
     * This will be mostly used by the changes on the query params.
     * One important thing is from now on the state is not saved anymore when we move from page to page using the menu.
     * If we want the previous stage we could navigate back using the browser
     *
     * One other important thing is that this will trigger when the query params update this component or when the user update this component.
     * That is the reason we make the equality check.
     *
     */
    @Input() public newValueTriggered$?: Observable<string | undefined>;

    @Output() public textOnSelection = new EventEmitter<string | undefined>();

    public searchControl: UntypedFormControl;
    public searchSubmitAttempt: boolean;
    public searchSelected: boolean;

    private _lastSearchValue: string;
    private _newValueSubscription$: Subscription;

    constructor() {
        this.searchSubmitAttempt = false;
        this.searchSelected = false;
    }

    @HostListener("document:keydown.escape", ["$event.target"])
    public onClick() {
        if (this.searchSelected) {
            this.deactivateSearch();
        }
    }

    ngOnInit(): void {
        this.syncValidators.push(Validators.maxLength(1000));
        let formControlValue = "";
        if (this.startingValue) {
            formControlValue = this.startingValue;
            this.searchSubmitAttempt = true;
        }

        this.searchControl = new UntypedFormControl(formControlValue, this.syncValidators);

        if (this.newValueTriggered$) {
            this._newValueSubscription$ = this.newValueTriggered$.subscribe((searchValue) => {
                if (searchValue) {
                    this.searchSubmitAttempt = true;
                    this.searchControl = new UntypedFormControl(searchValue, this.syncValidators);
                } else {
                    this.searchSubmitAttempt = false;
                    this.searchControl.reset();
                }
            });
        }
    }

    ngOnDestroy(): void {
        this._newValueSubscription$?.unsubscribe();
    }

    public searchFocus() {
        this.searchSelected = true;
    }

    public searchBlur() {
        this.searchSelected = false;
    }

    public activateSearch() {
        const searchValue = this.searchControl.value;
        if (!searchValue && this._lastSearchValue) {
            this.deactivateSearch();
        }

        if (searchValue && this.searchControl.valid && searchValue !== this._lastSearchValue) {
            this._lastSearchValue = searchValue;
            this.textOnSelection.emit(searchValue);
            if (this.clearAfterSubmit) {
                this.searchControl.reset();
                this.searchSubmitAttempt = false;
            } else {
                this.searchSubmitAttempt = true;
            }
        }
    }

    public deactivateSearch(): void {
        this._lastSearchValue = "";
        this.searchControl.reset();
        this.textOnSelection.emit(undefined);

        this.searchSubmitAttempt = false;
    }

    public onChange(): void {
        if (this.onChangeTriggered) {
            this.textOnSelection.emit(this.searchControl.value);
        }
    }
}
